import React, { useState, useEffect } from "react";
import { ShieldCheckIcon, RefreshCwIcon, BadgeCheckIcon } from "lucide-react";
import { Button } from "@component/button";
import { authResendCodeJobSeeker, authVerifyEmailJobSeeker } from "@service/auth";
import { markWhatsappOptIn } from "@service/whatsapp";
import { catchErrorLog } from "@util/sentry";
import { trackEvent } from "@util/mixpanel";
import { getCookie, setCookie } from "@util/cookie";

interface Props {
    email: string;
    onNextStep: () => void
}

export function CandidateEmailOTPVerify(props: Props) {
    const [otpValues, setOtpValues] = useState<string[]>(Array(6).fill(""));
    const [isVerifying, setIsVerifying] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [timeLeft, setTimeLeft] = useState(60);
    const [isVerified, setIsVerified] = useState(false);

    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        if (timeLeft > 0 && !isVerified) {
            const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [timeLeft, isVerified]);

    const handleChange = (index: number, value: string) => {
        // Only allow numbers
        if (!/^\d*$/.test(value)) return;

        const newOtpValues = [...otpValues];
        newOtpValues[index] = value;
        setOtpValues(newOtpValues);

        // Auto focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (
        index: number,
        e: React.KeyboardEvent<HTMLInputElement>
    ) => {
        // Handle backspace
        if (e.key === "Backspace" && !otpValues[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text/plain").trim();

        // Check if pasted content is a 6-digit number
        if (/^\d{6}$/.test(pastedData)) {
            const digits = pastedData.split("");
            setOtpValues(digits);

            // Focus the last input
            inputRefs.current[5]?.focus();
        }
    };

    const handleVerify = () => {
        const otp = otpValues.join("");

        // Check if OTP is complete
        if (otp.length !== 6) {
            setError("Please enter all 6 digits.");
            return;
        }

        setIsVerifying(true);
        setError(null);

        authVerifyEmailJobSeeker(
            props.email,
            otpValues.join("")
        ).then((resp) => {
            if (resp.data.code === 200) {
                setIsVerified(true);
                trackEvent('otp_verify_success', {
                    email: props.email,
                    otp: otpValues.join("")
                })
                setCookie("tempAuthToken", resp.data.data?.token?.access_token ?? "", import.meta.env.VITE_AUTH_TOKEN_EXPIRED_DAY);
                // Persist the WhatsApp opt-in captured on the apply form now that the
                // OTP is verified and a token exists (#2857) — the opt-in endpoint
                // needs the token, which only exists after auth/verify-jobseeker-email.
                // Intent + phone are carried in the __apply_job_guest__ cookie; the
                // candidate id in __USERID__. Best-effort — never disrupt verification.
                try {
                    // getCookie (universal-cookie) already returns the PARSED object —
                    // do NOT JSON.parse again (that threw and silently skipped the call).
                    const applyData = getCookie("__apply_job_guest__");
                    const candidateId = sessionStorage.getItem("__USERID__");
                    // Only persist when the cookie was created for THIS candidate — a
                    // stale quick-apply cookie must not leak consent into an unrelated
                    // verification — and flip the flag off afterwards so a later OTP
                    // verify can't re-fire it (#2857 review).
                    // No env flag here: the cookie's whatsapp_opt_in is only ever
                    // set when the checkbox was shown, which is gated per-company
                    // via company_whatsapp_enabled on the apply form.
                    if (
                        applyData?.whatsapp_opt_in &&
                        applyData?.phone_e164 &&
                        candidateId &&
                        String(applyData?.whatsapp_candidate_id) === candidateId
                    ) {
                        markWhatsappOptIn(candidateId, {
                            opted_in: true,
                            phone_e164: applyData.phone_e164,
                            source: "application_form",
                        }).then(() => {
                            // One-shot the cookie ONLY after the PATCH landed —
                            // clearing it eagerly erased the retry signal on a
                            // transient failure, silently dropping the consent of
                            // a user who explicitly opted in (#2896 review). On
                            // failure the flag stays set so a later OTP verify
                            // (same candidate — the whatsapp_candidate_id guard
                            // still applies) can retry the persist.
                            setCookie("__apply_job_guest__", JSON.stringify({
                                ...applyData,
                                whatsapp_opt_in: false,
                            }));
                        }).catch((e) => catchErrorLog(`WhatsApp opt-in persist failed: ${e}`));
                    }
                } catch (e) {
                    catchErrorLog(`WhatsApp opt-in persist failed: ${e}`);
                }
            } else {
                setIsVerified(false);
                setOtpValues(Array(6).fill(""));
                setError(resp.data.message);
            }
        }).catch((error) => {
            catchErrorLog(`failed to auth verify email, err: ${error}`);
            setIsVerified(false);
            setError(`Incorrect verification code.`);
        }).finally(() => {
            setIsVerifying(false);
        })
    };

    const handleResendCode = () => {
        setIsResending(true);
        setError(null);

        authResendCodeJobSeeker(
            props.email
        ).then((resp) => {
            if (resp.data.code !== 200) {
                setError(`Failed to resend OTP. Please try it again later.`);
            }
        }).catch((error) => {
            catchErrorLog(`failed to auth verify email, err: ${error}`);
            setError(`Failed to resend OTP. Please try it again later.`);
        }).finally(() => {
            setIsResending(false);
            setTimeLeft(60);
            setOtpValues(Array(6).fill(""));
            inputRefs.current[0]?.focus();
        })
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-[350px]">
            <div className="flex flex-col items-center justify-center text-center w-full max-w-md mx-auto">
                <div className="mb-2 md:mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-full">
                    <ShieldCheckIcon className="h-8 md:h-12 w-8 md:w-12 text-blue-500" />
                </div>
                <h2 className="text-lg md:text-3xl font-bold mb-2">
                    Verify your email
                </h2>
                <p className="mb-6">
                    We've sent a verification code to{" "}
                    <span className="font-medium text-blue-600">{props.email}</span>.
                </p>

                {!isVerified ? (
                    <>
                        <div className="flex justify-center gap-2 mb-6">
                            {otpValues.map((value, index) => (
                                <input
                                    key={index}
                                    ref={(el) => { inputRefs.current[index] = el }}
                                    type="text"
                                    maxLength={1}
                                    value={value}
                                    onChange={(e) => handleChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onPaste={index === 0 ? handlePaste : undefined}
                                    className="w-12 h-12 text-center text-xl font-bold border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    autoFocus={index === 0}
                                />
                            ))}
                        </div>

                        {error && (
                            <p className="text-red-500 dark:text-red-400 mb-4 text-sm">
                                {error}
                            </p>
                        )}

                        <Button
                            variant="default"
                            className="w-full mb-4"
                            onClick={handleVerify}
                            disabled={isVerifying || otpValues.some((v) => v === "")}
                        >
                            {isVerifying ? "Verifying..." : "Verify Email"}
                        </Button>

                        <div className="text-sm text-gray-500 dark:text-gray-400">
                            {timeLeft > 0 ? (
                                <p>Resend code in {timeLeft} seconds</p>
                            ) : (
                                <button
                                    onClick={handleResendCode}
                                    disabled={isResending}
                                    className="text-blue-600 dark:text-blue-400 hover:underline flex items-center justify-center gap-1 mx-auto"
                                >
                                    {isResending ? (
                                        <>
                                            <RefreshCwIcon className="h-4 w-4 animate-spin" />
                                            Resending...
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCwIcon className="h-4 w-4" />
                                            Resend code
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center">
                        <p className="text-black mb-6 flex items-center gap-2">
                            <BadgeCheckIcon className="h-5 w-5 text-green-600 dark:text-green-500 " />
                            Email verified successfully!
                        </p>
                        <Button variant="default" onClick={props.onNextStep} className="w-full rounded-full">
                            Continue
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
